"use client";

import React from "react";
import { Search, X, RotateCcw } from "lucide-react";
import type {
  HotelFilters,
  FilterLookups,
  LookupItem,
} from "@/types/hotel-filters";
import { DEFAULT_FILTERS } from "@/types/hotel-filters";

interface FilterSidebarProps {
  filters: HotelFilters;
  lookups: FilterLookups | null;
  onFilterChange: (filters: Partial<HotelFilters>) => void;
  onClose?: () => void;
  className?: string;
}

const PRICE_SLIDER_MAX = 60000; // LKR

// Static bar-height profile for the price distribution histogram
// (purely visual — swap for real bucketed data from the API when available)
const PRICE_HISTOGRAM = [
  15, 25, 35, 45, 60, 75, 90, 100, 95, 85, 70, 60, 50, 65, 80, 90, 85, 70, 55,
  45, 60, 75, 65, 50, 40, 55, 45, 35, 30, 25, 20, 15, 25, 20, 15, 10, 12, 8, 6,
  5,
];

export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters,
  lookups,
  onFilterChange,
  onClose,
  className = "",
}) => {
  const [expandedSections, setExpandedSections] = React.useState<
    Record<string, boolean>
  >({});
  // Local state for sidebar search only (doesn't filter hotels)
  const [sidebarSearchText, setSidebarSearchText] = React.useState("");

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Reset all filters to default values
  const handleResetFilters = () => {
    setSidebarSearchText("");
    onFilterChange(DEFAULT_FILTERS);
  };

  // Helper function to filter items by search text
  const filterBySearchText = (
    items: LookupItem[],
    searchText: string,
  ): LookupItem[] => {
    if (!searchText.trim()) return items;
    const lowerSearchText = searchText.toLowerCase();
    return items.filter((item) =>
      item.name.toLowerCase().includes(lowerSearchText),
    );
  };

  // Filter categories based on sidebar search text
  const filteredCategories = React.useMemo(() => {
    if (!lookups?.categories) return [];
    return filterBySearchText(lookups.categories, sidebarSearchText);
  }, [lookups?.categories, sidebarSearchText]);

  // Filter amenities based on sidebar search text
  const amenitiesByType = React.useMemo(() => {
    if (!lookups?.amenities) return {};
    const filteredAmenities = filterBySearchText(
      lookups.amenities,
      sidebarSearchText,
    );
    return filteredAmenities.reduce(
      (acc, amenity) => {
        const type = amenity.node_type || 0;
        if (!acc[type]) acc[type] = [];
        acc[type].push(amenity);
        return acc;
      },
      {} as Record<number, LookupItem[]>,
    );
  }, [lookups?.amenities, sidebarSearchText]);

  // Filter access modes based on sidebar search text
  const filteredAccessModes = React.useMemo(() => {
    if (!lookups?.accessModes) return [];
    return filterBySearchText(lookups.accessModes, sidebarSearchText);
  }, [lookups?.accessModes, sidebarSearchText]);

  // Filter payment methods based on sidebar search text
  const filteredPaymentMethods = React.useMemo(() => {
    if (!lookups?.paymentMethods) return [];
    return filterBySearchText(lookups.paymentMethods, sidebarSearchText);
  }, [lookups?.paymentMethods, sidebarSearchText]);

  // Check if any results are found
  const hasSearchResults = React.useMemo(() => {
    if (!sidebarSearchText.trim()) return true;
    return (
      filteredCategories.length > 0 ||
      Object.values(amenitiesByType).some(
        (amenities) => amenities.length > 0,
      ) ||
      filteredAccessModes.length > 0 ||
      filteredPaymentMethods.length > 0
    );
  }, [
    sidebarSearchText,
    filteredCategories,
    amenitiesByType,
    filteredAccessModes,
    filteredPaymentMethods,
  ]);

  const amenityTypeLabels: Record<number, string> = {
    1: "General Amenities",
    2: "Room Features",
    3: "Services",
    4: "Activities",
    5: "Food & Drink",
    6: "Security & Services",
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 sticky top-4 self-start min-w-0 w-full ${className}`}
    >
      <div className="flex items-center justify-between p-4 md:p-6 pb-0 lg:hidden">
        <h2 className="text-lg font-semibold">Filters</h2>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="p-4 md:p-6">
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search filters"
              value={sidebarSearchText}
              onChange={(e) => setSidebarSearchText(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <button
            onClick={handleResetFilters}
            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset Filters
          </button>
        </div>

        <div className="mb-7 min-w-0 w-full">
          <h3 className="font-semibold text-gray-900 mb-1">
            Your budget (per night)
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            LKR {filters.priceRange.min.toLocaleString()} —{" "}
            {filters.priceRange.max >= PRICE_SLIDER_MAX
              ? `LKR ${PRICE_SLIDER_MAX.toLocaleString()}+`
              : `LKR ${filters.priceRange.max.toLocaleString()}`}
          </p>

          <div className="relative w-full min-w-0">
            {/* Price distribution histogram */}
            <div className="flex items-end gap-[2px] h-12 mb-1 px-0.5">
              {PRICE_HISTOGRAM.map((height, i) => {
                const barPosition = (i / PRICE_HISTOGRAM.length) * 100;
                const inRange =
                  barPosition >=
                    (filters.priceRange.min / PRICE_SLIDER_MAX) * 100 &&
                  barPosition <=
                    (filters.priceRange.max / PRICE_SLIDER_MAX) * 100;
                return (
                  <div
                    key={i}
                    className={`flex-1 rounded-sm transition-colors ${
                      inRange ? "bg-purple-300" : "bg-gray-200"
                    }`}
                    style={{ height: `${height}%` }}
                  />
                );
              })}
            </div>

            {/* Track + thumbs */}
            <div className="relative overflow-hidden py-1 px-1">
              <div className="relative h-1.5 rounded-full bg-gray-200">
                <div
                  className="absolute h-1.5 rounded-full bg-purple-600"
                  style={{
                    left: `${(filters.priceRange.min / PRICE_SLIDER_MAX) * 100}%`,
                    right: `${100 - (filters.priceRange.max / PRICE_SLIDER_MAX) * 100}%`,
                  }}
                />
              </div>
              {/* Min thumb — raised above max thumb once past the midpoint
                  so whichever handle is closer to the right edge stays grabbable */}
              <input
                type="range"
                min={0}
                max={PRICE_SLIDER_MAX}
                step={500}
                value={filters.priceRange.min}
                onChange={(e) => {
                  const val = Math.min(
                    parseInt(e.target.value) || 0,
                    filters.priceRange.max - 500,
                  );
                  onFilterChange({
                    priceRange: { ...filters.priceRange, min: val },
                  });
                }}
                aria-label="Minimum price"
                style={{
                  zIndex: filters.priceRange.min > PRICE_SLIDER_MAX / 2 ? 5 : 3,
                }}
                className="price-range-thumb absolute top-1 left-0 right-0 w-full max-w-full h-1.5 appearance-none bg-transparent pointer-events-none"
              />
              {/* Max thumb */}
              <input
                type="range"
                min={0}
                max={PRICE_SLIDER_MAX}
                step={500}
                value={filters.priceRange.max}
                onChange={(e) => {
                  const val = Math.max(
                    parseInt(e.target.value) || 0,
                    filters.priceRange.min + 500,
                  );
                  onFilterChange({
                    priceRange: { ...filters.priceRange, max: val },
                  });
                }}
                aria-label="Maximum price"
                style={{
                  zIndex: filters.priceRange.min > PRICE_SLIDER_MAX / 2 ? 3 : 5,
                }}
                className="price-range-thumb absolute top-1 left-0 right-0 w-full max-w-full h-1.5 appearance-none bg-transparent pointer-events-none"
              />
            </div>

            {/* Min/Max labels under the track */}
            <div className="flex items-center justify-between mt-2 text-xs text-gray-400 font-medium">
              <span>LKR 0</span>
              <span>LKR {PRICE_SLIDER_MAX.toLocaleString()}+</span>
            </div>
          </div>
        </div>

        {lookups?.categories && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">Property Types</h3>
            {filteredCategories.length > 0 ? (
              <>
                <div className="space-y-3">
                  {filteredCategories
                    .slice(0, expandedSections["categories"] ? undefined : 10)
                    .map((category) => (
                      <label
                        key={category.code}
                        className="flex items-center cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.categories.includes(
                            category.id.toString(),
                          )}
                          onChange={(e) => {
                            const categoryId = category.id.toString();
                            const newCategories = e.target.checked
                              ? [...filters.categories, categoryId]
                              : filters.categories.filter(
                                  (c) => c !== categoryId,
                                );
                            onFilterChange({ categories: newCategories });
                          }}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          {category.name}
                        </span>
                      </label>
                    ))}
                </div>
                {filteredCategories.length > 10 && (
                  <button
                    onClick={() => toggleSection("categories")}
                    className="text-sm text-purple-600 hover:text-purple-700 mt-2"
                  >
                    {expandedSections["categories"]
                      ? "See less"
                      : `See more (${filteredCategories.length - 10})`}
                  </button>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500 italic">
                No property types match your search
              </p>
            )}
          </div>
        )}

        {lookups?.amenities && Object.keys(amenitiesByType).length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">Amenities</h3>
            {Object.keys(amenitiesByType).length > 0 ? (
              Object.entries(amenitiesByType).map(([nodeType, amenities]) => (
                <div key={nodeType} className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    {amenityTypeLabels[Number(nodeType)] || "Other"}
                  </h4>
                  <div className="space-y-2">
                    {amenities
                      .slice(
                        0,
                        expandedSections[`amenity-${nodeType}`]
                          ? undefined
                          : 10,
                      )
                      .map((amenity) => (
                        <label
                          key={amenity.code}
                          className="flex items-center cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={filters.amenities.includes(amenity.code)}
                            onChange={(e) => {
                              const newAmenities = e.target.checked
                                ? [...filters.amenities, amenity.code]
                                : filters.amenities.filter(
                                    (a) => a !== amenity.code,
                                  );
                              onFilterChange({ amenities: newAmenities });
                            }}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            {amenity.name}
                          </span>
                        </label>
                      ))}
                  </div>
                  {amenities.length > 10 && (
                    <button
                      onClick={() => toggleSection(`amenity-${nodeType}`)}
                      className="text-sm text-purple-600 hover:text-purple-700 mt-2"
                    >
                      {expandedSections[`amenity-${nodeType}`]
                        ? "See less"
                        : `See more (${amenities.length - 10})`}
                    </button>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 italic">
                No amenities match your search
              </p>
            )}
          </div>
        )}

        {/* {lookups?.accessModes && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">Access Modes</h3>
            {filteredAccessModes.length > 0 ? (
              <>
                <div className="space-y-3">
                  {filteredAccessModes
                    .slice(0, expandedSections["accessModes"] ? undefined : 10)
                    .map((mode) => (
                      <label
                        key={mode.code}
                        className="flex items-center cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.accessModes.includes(mode.code)}
                          onChange={(e) => {
                            const newModes = e.target.checked
                              ? [...filters.accessModes, mode.code]
                              : filters.accessModes.filter(
                                  (m) => m !== mode.code,
                                );
                            onFilterChange({ accessModes: newModes });
                          }}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          {mode.name}
                        </span>
                      </label>
                    ))}
                </div>
                {filteredAccessModes.length > 10 && (
                  <button
                    onClick={() => toggleSection("accessModes")}
                    className="text-sm text-purple-600 hover:text-purple-700 mt-2"
                  >
                    {expandedSections["accessModes"]
                      ? "See less"
                      : `See more (${filteredAccessModes.length - 10})`}
                  </button>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500 italic">
                No access modes match your search
              </p>
            )}
          </div>
        )} */}

        {/* {lookups?.paymentMethods && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">
              Payment Methods
            </h3>
            {filteredPaymentMethods.length > 0 ? (
              <>
                <div className="space-y-3">
                  {filteredPaymentMethods
                    .slice(
                      0,
                      expandedSections["paymentMethods"] ? undefined : 10,
                    )
                    .map((method) => (
                      <label
                        key={method.code}
                        className="flex items-center cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.paymentMethods.includes(method.code)}
                          onChange={(e) => {
                            const newMethods = e.target.checked
                              ? [...filters.paymentMethods, method.code]
                              : filters.paymentMethods.filter(
                                  (m) => m !== method.code,
                                );
                            onFilterChange({ paymentMethods: newMethods });
                          }}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          {method.name}
                        </span>
                      </label>
                    ))}
                </div>
                {filteredPaymentMethods.length > 10 && (
                  <button
                    onClick={() => toggleSection("paymentMethods")}
                    className="text-sm text-purple-600 hover:text-purple-700 mt-2"
                  >
                    {expandedSections["paymentMethods"]
                      ? "See less"
                      : `See more (${filteredPaymentMethods.length - 10})`}
                  </button>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500 italic">
                No payment methods match your search
              </p>
            )}
          </div>
        )} */}

        {sidebarSearchText.trim() && !hasSearchResults && (
          <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
            <p className="text-sm text-blue-800">
              No filters match your search term "
              <strong>{sidebarSearchText}</strong>". Try searching for different
              amenities or property types.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
